import * as React from "react";
import { gql, useQuery } from "@apollo/client";
import {
  ViewCategoryOffersQueryQueryVariables,
  ViewCategoryOffersQueryQuery
} from "../../../schemaTypes";

export const viewCategoryOffersQuery = gql`
  query ViewCategoryOffersQuery(
    $categoryId: String!
    $offset: Int!
    $limit: Int!
  ) {
    categoryOffers(
      categoryId: $categoryId
      offset: $offset, 
      limit: $limit
    ) {
      id
      title
      description
      bannerUrl
      category {
        id
        name
        imgSrc
        createdBy{
          id
          role
        }
        createdAt
        updatedAt
      }
      offerType
      discountType
      discountValue
      partnerVoucher
      referralUrl
      status
      usageLimitPerUser
      totalUsageLimit
      startDate
      endDate
      community{
        id
        name
        alias
      }
      redemptions
      revenueGenerated
      clickThroughs
      featured
      createdBy{
        id
        role
        admin {
          id
          shortId
          firstname
          lastname
          email
          phone
          merchantProfile {
            id
            businessName
            businessType
            regCountry
            businessRegNumber
            taxId
            address
            addressAlt
            city
            country
            postcode
            companyName
            username
            industry {
              id
              name
              createdBy{
                id
                role
              }
              createdAt
              updatedAt
            }
            companyLocation
            logoUrl
            bannerUrl
            description
            commissionRate
            createdAt
            updatedAt
          }
          createdAt
          updatedAt
        }
      }
      # njoyCode {
      #   id
      #   code
      #   redeemedAt
      #   createdAt
      #   updatedAt
      # }
      createdAt
      updatedAt
    }
  }
`;

export const newCategoryOfferSubscription = gql`
  subscription NewCategoryOfferSubscription($categoryId: String!) {
    newCategoryOffer(categoryId: $categoryId) {
      id
      title
      description
      bannerUrl
      category {
        id
        name
        imgSrc
        createdBy{
          id
          role
        }
        createdAt
        updatedAt
      }
      offerType
      discountType
      discountValue
      partnerVoucher
      referralUrl
      status
      usageLimitPerUser
      totalUsageLimit
      startDate
      endDate
      community{
        id
        name
        alias
      }
      redemptions
      revenueGenerated
      clickThroughs
      featured
      createdBy{
        id
        role
        admin {
          id
          shortId
          firstname
          lastname
          email
          phone
          merchantProfile {
            id
            businessName
            businessType
            regCountry
            businessRegNumber
            taxId
            address
            addressAlt
            city
            country
            postcode
            companyName
            username
            industry {
              id
              name
              createdBy{
                id
                role
              }
              createdAt
              updatedAt
            }
            companyLocation
            logoUrl
            bannerUrl
            description
            commissionRate
            createdAt
            updatedAt
          }
          createdAt
          updatedAt
        }
      }
      # njoyCode {
      #   id
      #   code
      #   redeemedAt
      #   createdAt
      #   updatedAt
      # }
      createdAt
      updatedAt
    }
  }
`;

export function useViewCategoryOffers(variables: ViewCategoryOffersQueryQueryVariables) {
  const { data, loading, fetchMore, subscribeToMore } = useQuery<ViewCategoryOffersQueryQuery, ViewCategoryOffersQueryQueryVariables>(
    viewCategoryOffersQuery,
    { variables }
  );

  React.useEffect(() => {
    if (!subscribeToMore) return;
    const unsubscribe = subscribeToMore({
      document: newCategoryOfferSubscription,
      variables: { categoryId: variables.categoryId },
      updateQuery: (prev, { subscriptionData }) => {
        if (!subscriptionData.data) return prev;
        return {
          ...prev,
          categoryOffers: [
            ...prev.categoryOffers,
            (subscriptionData.data as any).newCategoryOffer
          ]
        };
      }
    });
    return () => unsubscribe();
  }, [subscribeToMore, variables.categoryId]);

  // Use the generated type for each offer
  type CategoryOffer = ViewCategoryOffersQueryQuery['categoryOffers'][number];
  const categoryOffers: CategoryOffer[] = data?.categoryOffers ?? [];
  let hasMoreCategoryOffers = categoryOffers.length % variables.limit === 0;
  if (categoryOffers.length <= variables.offset) {
    hasMoreCategoryOffers = false;
  }

  const loadMore = () => {
    fetchMore({
      variables: {
        ...variables,
        offset: categoryOffers.length
      },
      updateQuery: (prev, { fetchMoreResult }) => {
        if (!fetchMoreResult) return prev;
        return {
          ...prev,
          categoryOffers: [
            ...prev.categoryOffers,
            ...fetchMoreResult.categoryOffers
          ]
        };
      }
    });
  };

  return {
    categoryOffers,
    loading,
    loadMore,
    hasMoreCategoryOffers
  };
}